import { useEffect, useState } from 'react';

import { Combobox, HookFormResolver, Text, TextArea } from '@dec/shared';
import { Col, Row } from '@dec/shared';
import { Button } from '@dec/shared';
import { ButtonAction } from '@dec/shared';
import { type HookFormMethods } from '@dec/shared';
import { BtnToolbar } from '@dec/shared';
import { ConfirmDialog } from 'app/core/dialog';
import { label as lblCommon } from 'app/modules/translation';
import { ButtonGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useCatalogValueList } from '@/app/modules/configuration/catalogs/hooks/useCatalogValueList';
import {
  type CatalogModel,
  catalogName,
  type CatalogValue
} from '@/app/modules/configuration/catalogs/models/Catalog';

import { useTaxpayerRequestStore } from '../../stores/useTaxpayerRequestStore';
import { GivenNamesAndSurNames } from '../components/given-names-and-sur-names';
import ProgressBar from '../components/progress-bar';
import { ValidationsResolver } from '../rules';
import { rulesGeneralData } from '../rules/general';
import { label } from '../translation';
import { type GeneralTaxpayer } from '../types';
import VisibleComponent from '../visible-component';

import { type ProgressStepProps } from './types';

interface GeneralDataProps {
  onSubmit: (data: any) => void;
  countries: Array<CatalogModel>;
  personTypes: Array<CatalogModel>;
  currentData: GeneralTaxpayer;
  readonly?: boolean;
  disablePersonType?: boolean;
}

export const GeneralData = ({
  onSubmit,
  countries,
  personTypes,
  currentData,
  readonly,
  disablePersonType
}: GeneralDataProps) => {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<GeneralTaxpayer>();
  const [parent, setParent] = useState(currentData?.politicalDivision?.id ?? '');

  const politicalDivisions: Array<CatalogValue> = [];

  // const [data: politicalDivisions, isLoading: pdLoading, error: pdError } =
  //   useCatalogValueListByParent(parent);

  useEffect(() => {
    // if (currentData?.politicalDivision?.id) {
    //   execute();
    // }
    setState(currentData);
  }, []);

  // useEffect(() => {
  //   if (state !== undefined) {
  //     execute();
  //   }
  // }, [parent]);

  if (state === undefined) {
    return <></>;
  }

  return (
    <ValidationsResolver rules={rulesGeneralData}>
      {({ validations }: any) => {
        return (
          <HookFormResolver
            nameForm={'formGeneralData'}
            onSubmit={onSubmit}
            validations={validations}
            initialValues={state}
          >
            {({ formState: { errors }, reset, setValue, control, watch }: HookFormMethods) => {
              const personType = watch('personType');
              return (
                <>
                  {' '}
                  <VisibleComponent fieldCode="personType" hideOnly={true}>
                    <Row>
                      <Col>
                        <Combobox
                          label={lblCommon('personType')}
                          name="personType"
                          data={personTypes}
                          defaultValue={personTypes[0]}
                          code="id"
                          display="name"
                          readOnly={readonly || disablePersonType}
                        />
                      </Col>
                    </Row>
                  </VisibleComponent>
                  <GivenNamesAndSurNames
                    propetyName="taxpayerName"
                    enable={personType?.code == 'individual'}
                    readonly={readonly}
                  />
                  <Row>
                    <Col>
                      <Text
                        name="taxpayerName"
                        label={
                          personType?.code === 'business'
                            ? label(t, 'businessName')
                            : label(t, 'fullName')
                        }
                        readonly={readonly || personType?.code === 'individual'}
                      />
                    </Col>
                  </Row>
                  {personType?.code === 'business' && (
                    <Row>
                      <VisibleComponent fieldCode="tradeName">
                        <Col>
                          <Text
                            readonly={readonly}
                            name="tradeName"
                            label={lblCommon('tradeName')}
                          />
                        </Col>
                      </VisibleComponent>
                    </Row>
                  )}
                  <VisibleComponent fieldCode="address">
                    <Row>
                      <Col>
                        <TextArea readOnly={readonly} label={lblCommon('address')} name="address" />
                      </Col>
                    </Row>
                  </VisibleComponent>
                  <Row>
                    <Col>
                      <Combobox
                        code="id"
                        display="name"
                        data={countries}
                        label={lblCommon('country')}
                        name="country"
                        readOnly={readonly}
                        child="politicalDivision"
                      />
                    </Col>
                    <VisibleComponent fieldCode="politicalDivision">
                      <Col>
                        <Combobox
                          code="id"
                          display="name"
                          data={politicalDivisions}
                          label={lblCommon('politicalDivision')}
                          name="politicalDivision"
                          readOnly={readonly}
                        />
                      </Col>
                    </VisibleComponent>
                  </Row>
                  <Row>
                    <VisibleComponent fieldCode="city">
                      <Col>
                        <Text readonly={readonly} name="city" label={lblCommon('city')} />
                      </Col>
                    </VisibleComponent>
                    <VisibleComponent fieldCode="zipCode">
                      <Col>
                        <Text readonly={readonly} name="zipCode" label={lblCommon('zipCode')} />
                      </Col>
                    </VisibleComponent>
                  </Row>
                  <VisibleComponent fieldCode="website">
                    <Row>
                      <Col>
                        <Text
                          readonly={readonly}
                          name="website"
                          label={lblCommon('website')}
                          placeholder="https://www.site01.com, www.site02.com"
                        />
                      </Col>
                    </Row>
                  </VisibleComponent>
                </>
              );
            }}
          </HookFormResolver>
        );
      }}
    </ValidationsResolver>
  );
};

export const GeneralDataTab = (props: ProgressStepProps) => {
  const currentData = useTaxpayerRequestStore((s) => s.form.general);
  const addGeneralData = useTaxpayerRequestStore((s) => s.addGeneralData);
  const reset = useTaxpayerRequestStore((s) => s.reset);
  const {
    data: countries,
    isLoading: loadingCountry,
    error: errorCountry
  } = useCatalogValueList(catalogName.countries);
  const {
    data: personTypes,
    isLoading: loadingPersonTypes,
    error: errorPersonTypes
  } = useCatalogValueList(catalogName.personTypes);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const onSubmit = (data: any) => {
    addGeneralData(data);
    props.nextStep();
  };

  if (loadingCountry || errorCountry || loadingPersonTypes || errorPersonTypes) {
    return <></>;
  }
  const cancel = () => {
    reset();
    navigate('/');
  };
  return (
    <>
      {' '}
      <GeneralData
        currentData={currentData as GeneralTaxpayer}
        personTypes={personTypes || []}
        countries={(countries || []).filter((x) => x.additionalFields?.excludeGeneral !== true)}
        onSubmit={onSubmit}
      />
      <ProgressBar percentage={props.percentage} />
      <BtnToolbar>
        <ButtonGroup>
          <Button
            action={ButtonAction.cancel}
            onClick={async () => {
              const ok = await ConfirmDialog({
                titleModal: t('common.dialog.warning-title'),
                message: t('common.messages.warningCancelOperation'),
                okButton: t('common.label.ok'),
                cancelButton: t('common.label.cancel')
              });
              if (ok) {
                cancel();
              }
            }}
          />
        </ButtonGroup>
        <ButtonGroup>
          <Button
            form="formGeneralData"
            variant={'primary'}
            action={ButtonAction.next}
            type={'submit'}
          />
        </ButtonGroup>
      </BtnToolbar>
    </>
  );
};
